import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Field, FormSummary, RequiredLegend, focusFirstError, clean } from "@/components/landing/Field";
import { adminApi, apiError, setToken } from "@/lib/api";

const INITIAL = { current_password: "", new_password: "", confirm_password: "" };
const FIELDS = [
  ["current_password", "Mot de passe actuel", "current-password"],
  ["new_password", "Nouveau mot de passe (10 caractères minimum)", "new-password"],
  ["confirm_password", "Confirmer le nouveau mot de passe", "new-password"],
];

export const ChangePasswordDialog = () => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const submit = async (ev) => {
    ev.preventDefault();
    const e = clean({
      current_password: !f.current_password && "Le mot de passe actuel est obligatoire.",
      new_password: f.new_password.length < 10 && "Le nouveau mot de passe doit comporter au moins 10 caractères.",
      confirm_password: (!f.confirm_password || f.confirm_password !== f.new_password) && "La confirmation ne correspond pas au nouveau mot de passe.",
    });
    setErrors(e);
    if (Object.keys(e).length) return focusFirstError(ev.currentTarget);
    setSaving(true);
    try {
      const { data } = await adminApi.post("/password", f);
      setToken(data.token);
      toast.success("Mot de passe modifié. Les autres sessions ont été déconnectées.");
      setF(INITIAL);
      setOpen(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setF(INITIAL); setErrors({}); } }}>
      <DialogTrigger asChild>
        <button type="button" data-testid="admin-change-password-button" className="btn-ghost-ice !py-2">
          <KeyRound className="w-4 h-4" /> <span className="hidden md:inline">Changer mon mot de passe</span>
        </button>
      </DialogTrigger>
      <DialogContent data-testid="change-password-dialog" className="sm:max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Changer mon mot de passe</DialogTitle>
          <DialogDescription>Les autres sessions ouvertes seront déconnectées.</DialogDescription>
          <RequiredLegend testId="change-password-legend" />
        </DialogHeader>
        <form onSubmit={submit} noValidate className="flex flex-col gap-4">
          {FIELDS.map(([k, label, ac]) => (
            <Field key={k} id={`pwd-${k}`} label={label} required error={errors[k]}>
              <input id={`pwd-${k}`} type="password" autoComplete={ac} data-testid={`pwd-${k}-input`} value={f[k]} aria-invalid={!!errors[k]}
                onChange={(e) => { setF({ ...f, [k]: e.target.value }); setErrors({}); }} className="field-input" />
            </Field>
          ))}
          <FormSummary errors={errors} testId="change-password-summary" />
          <button type="submit" disabled={saving} data-testid="change-password-submit-button" className="btn-heat !py-2.5 disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer le nouveau mot de passe"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
